import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Drawer, Layout, Tooltip } from "antd";
import CustomIcon from "./CustomIcon";
import { GlobalSidebarContent, GlobalSidebarRail } from "./GlobalSidebar";
import { ANALYTICS_KEYS } from "../../utills/analyticsKeys";

const { Content } = Layout;
const GLOBAL_SIDEBAR_HIDDEN_KEY = "uxarmy-global-sidebar-hidden";
const GlobalSidebarControlsContext = createContext(null);

export default function GlobalAppShell({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isRailHidden, setIsRailHidden] = useState(() => localStorage.getItem(GLOBAL_SIDEBAR_HIDDEN_KEY) === "1");
  const { pathname } = location;
  const showSidebarControls =
    /^\/test\/[^/]+(?:\/.*)?$/.test(pathname) ||
    /^\/moderated\/[^/]+\/report(?:\/.*)?$/.test(pathname) ||
    /^\/tests\/[^/]+(?:\/.*)?$/.test(pathname) ||
    /^\/template\/[^/]+(?:\/.*)?$/.test(pathname);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if ((pathname === "/" || !showSidebarControls) && isRailHidden) {
      setIsRailHidden(false);
      localStorage.removeItem(GLOBAL_SIDEBAR_HIDDEN_KEY);
    }
  }, [pathname, isRailHidden, showSidebarControls]);

  const hideRail = () => {
    setIsRailHidden(true);
    localStorage.setItem(GLOBAL_SIDEBAR_HIDDEN_KEY, "1");
    setOpen(false);
  };

  const showRail = () => {
    setIsRailHidden(false);
    localStorage.removeItem(GLOBAL_SIDEBAR_HIDDEN_KEY);
  };

  const openDrawer = () => {
    setOpen(true);
  };

  const controlsValue = {
    showSidebarControls,
    isRailHidden,
    open,
    showRail,
    hideRail,
    openDrawer,
  };

  if (pathname === "/" || pathname?.toLowerCase() === "/onboarding") return children;

  return (
    <GlobalSidebarControlsContext.Provider value={controlsValue}>
      <Layout hasSider className='global-app-shell h-100vh'>
        {!isRailHidden && !open && (
          <GlobalSidebarRail
            onExpand={() => setOpen(true)}
            onHide={hideRail}
            showHideButton={showSidebarControls}
            showExpandButton={!showSidebarControls}
            showTopControls
          />
        )}

        <Content className='global-app-shell__content'>{children}</Content>

        <Drawer
          open={open}
          size={300}
          closable={false}
          placement='left'
          onClose={() => setOpen(false)}
          className='global-sidebar-drawer'
          styles={{ body: { padding: 0 }, section: { overflow: "hidden" } }}
        >
          <GlobalSidebarContent onNavigate={() => setOpen(false)} onCollapse={() => setOpen(false)} />
        </Drawer>
      </Layout>
    </GlobalSidebarControlsContext.Provider>
  );
}

function useGlobalSidebarControls() {
  const context = useContext(GlobalSidebarControlsContext);

  if (!context) {
    throw new Error("Global sidebar controls must be used within GlobalAppShell");
  }

  return context;
}

function GlobalSidebarRailButton({ className = "" }) {
  const { showSidebarControls, isRailHidden, open, showRail, hideRail } = useGlobalSidebarControls();

  if (!showSidebarControls || open || !isRailHidden) return null;

  return (
    <Tooltip title={isRailHidden ? "Show Sidebar" : "Hide Sidebar"} placement='right'>
      <Button
        type='text'
        size='small'
        className={className?.trim()}
        icon={<CustomIcon name='menu_01' width={18} height={18} />}
        onClick={isRailHidden ? showRail : hideRail}
        data-attr={ANALYTICS_KEYS.GLOBAL_SIDEBAR.EXPAND_SIDEBAR}
      />
    </Tooltip>
  );
}

function GlobalSidebarDrawerButton({ className = "" }) {
  const { showSidebarControls, open, openDrawer } = useGlobalSidebarControls();

  if (!showSidebarControls || open) return null;

  return (
    <Button
      type='text'
      size='small'
      className={className?.trim()}
      icon={<CustomIcon name='panel_left_open' width={18} height={18} />}
      onClick={openDrawer}
      data-attr={ANALYTICS_KEYS.GLOBAL_SIDEBAR.EXPAND_SIDEBAR}
    />
  );
}

export function GlobalSidebarControl() {
  const useRailButton = true;

  if (useRailButton) return <GlobalSidebarRailButton />;
  else return <GlobalSidebarDrawerButton />;
}
